import { useCallback, useEffect, useRef, type FocusEvent, type RefObject, type SubmitEvent } from "react";

import { useGo } from "../app/use-go.js";

/** 探す窓の欄（隠し欄は含めない）。 */
type Field = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

const isField = (element: unknown): element is Field =>
    (element instanceof HTMLInputElement && element.type !== "hidden")
    || element instanceof HTMLSelectElement
    || element instanceof HTMLTextAreaElement;

/** 押す前の欄と、カーソルの位置。 */
interface Caret {
    readonly field: Field;
    readonly start: number | null;
    readonly end: number | null;
}

const caretOf = (field: Field): Caret => (field instanceof HTMLSelectElement
    ? { field, start: null, end: null }
    : { field, start: field.selectionStart, end: field.selectionEnd });

/** 押す前の欄へフォーカスを戻す。<b>スクロールは動かさない</b>（<c>preventScroll</c>）。 */
const restore = ({ field, start, end }: Caret): void => {
    if (!field.isConnected) {
        return;
    }
    if (document.activeElement !== field) {
        field.focus({ preventScroll: true });
    }
    if (start !== null && end !== null && !(field instanceof HTMLSelectElement)) {
        try {
            field.setSelectionRange(start, end);
        } catch {
            // 日付・数の欄はカーソルの位置を持たない（ブラウザが断る）
        }
    }
};

export interface SearchForm {
    /** <c>&lt;form method="get" {...search.props}&gt;</c> と渡す。 */
    readonly props: {
        readonly ref: RefObject<HTMLFormElement | null>;
        readonly onSubmit: (event: SubmitEvent<HTMLFormElement>) => void;
        readonly onFocus: (event: FocusEvent<HTMLFormElement>) => void;
    };
}

/**
 * 探す窓（<c>method="get"</c> のフォーム）。<b>どの検索も同じ動きにする</b>（2026-10-10 に利用者が決めた。docs/01 §8）。
 *
 * - <b>欄をクエリにして同じ画面を開き直す</b>——条件は URL に載る（戻る・共有・再読み込みで同じ結果になる）。
 *   空の欄は載せない（「条件なし」と同じ）。<c>keepEmpty</c> なら空の欄も載せる（ユーザ管理。API が受ける形）
 * - <b>送った後もフォーカスは押す前の欄に残り、カーソルの位置も残る。</b>検索のボタンを押したときも欄へ戻す。
 *   画面は作り直さない（クエリだけが変わる移り方。<c>app/scroll-on-move.tsx</c>）ので、スクロールも動かない
 * - <b>欄の値は届いた条件（<c>values</c>。欄の名前ごと）に合わせる。</b>欄を作り直して書き戻すとフォーカスが外れるので、
 *   値だけを書き換える。クリア・戻るで条件が変わったときに前の語を残さない。
 *   <b>自分で送った条件が届くまでに打ち足している欄には触らない</b>（フォーカスがあり、届いた値が送った値と同じとき）。
 *   クリアのリンクを押してもフォーカスが欄に残るブラウザがある（Safari など）ので、フォーカスだけでは打ちかけと見なさない
 */
export const useSearchForm = (
    path: string,
    values: Readonly<Record<string, string>>,
    keepEmpty = false,
): SearchForm => {
    const go = useGo();
    const ref = useRef<HTMLFormElement | null>(null);
    const last = useRef<Field | null>(null);
    // 最後に送った欄の値（欄の名前ごと）。届いた条件がこれと違えば、送った後に条件が外から変わった（クリア・戻る）
    const submitted = useRef<Readonly<Record<string, string>> | null>(null);

    // 値の中身が変わったときだけ書き戻す（描き直すたびに書くと、打ちかけの欄を消す）
    const served = JSON.stringify(values);
    useEffect(() => {
        const form = ref.current;
        if (form === null) {
            return;
        }
        const wanted = JSON.parse(served) as Record<string, string>;
        for (const [name, value] of Object.entries(wanted)) {
            const field = form.elements.namedItem(name);
            const typing = field === document.activeElement && submitted.current?.[name] === value;
            if (isField(field) && !typing && field.value !== value) {
                field.value = value;
            }
        }
    }, [served]);

    const onFocus = useCallback((event: FocusEvent<HTMLFormElement>) => {
        if (isField(event.target)) {
            last.current = event.target;
        }
    }, []);

    const onSubmit = useCallback((event: SubmitEvent<HTMLFormElement>) => {
        event.preventDefault();

        const query = new URLSearchParams();
        const fields = new FormData(event.currentTarget);
        const sent: Record<string, string> = {};
        for (const [name, value] of fields) {
            if (typeof value !== "string") {
                continue;
            }
            sent[name] = value;
            if (keepEmpty || value !== "") {
                query.append(name, value);
            }
        }
        submitted.current = sent;
        const caret = last.current === null ? null : caretOf(last.current);
        const text = query.toString();
        go(text === "" ? path : `${path}?${text}`);
        if (caret !== null) {
            restore(caret);
        }
    }, [go, path, keepEmpty]);

    return { props: { ref, onSubmit, onFocus } };
};
